<template>
  <div id="root">
    <h2 v-if="error">
      {{ error }}
    </h2>
    <h2
      :class="{
        'display-mobile': true
      }"
      v-if="!error"
    >
      Please fill out graduation records on a desktop or laptop.
    </h2>
    <div
      :class="{
        'display-desktop': true
      }"
      v-if="!error"
      v-loading="!isReady"
      element-loading-text="Loading..."
      element-loading-spinner="el-icon-loading"
    >
      <div>
        <h1 class="text-center" style="display: inline;">
          Here are the seniors on your campus.
        </h1>
        <el-button
          v-show="modals.isFinished"
          :type="'warning'"
          round
          style="float:right;"
          @click="triggerAnimation()"
        >
          <h4
            style="margin-top:0; font-size:1.25rem;"
          >
            Are you finished?
          </h4>
        </el-button>
      </div>
      <div style="width:100%;">
        <el-slider
          v-model="stepProgress"
          class="slider-success"
          :step="50"
          show-stops
        />
      </div>
      <div style="display:flex; justify-content:space-between;">
        <div class="prog-step" style="width:30%">
          <span>Step 1:</span> Move seniors into categories.
        </div>
        <div class="prog-step" style="width:30%">
          <span>Step 2:</span> Fill out <i class="el-icon-phone" />, <i class="el-icon-message" />,
          and vocations for graduating seniors.
        </div>
        <div class="prog-step" style="width:30%">
          <span>Step 3:</span> Submit any freshmen, sophomores, and juniors entering seminary or religious life.
          <div>
            <el-button
              :type="'primary'"
              round
              @click="getEligibleStudents(); modals.vocations = true"
            >
              {{ countVocationStudents }} vocations
            </el-button>
          </div>
        </div>
      </div>
      <div class="container">
        <div class="row">
          <div
            class="col-md-2"
            v-if="showUncategorizedBucket"
          >
            <h4 class="text-center">
              Are they graduating?
            </h4>
            <p v-if="showUncategorizedBucket" class="text-center">
              Don't see someone in this list?
              <span
                class="clickable-paragraph emphasize-clickable"
                @click="getEligibleStudents(); modals.newGradForm = true"
              >
                Click here to add them!
              </span>
            </p>
          </div>
          <div
            :class="{
              'col-md-6': showUncategorizedBucket,
              'col-md-8': !showUncategorizedBucket,
              'min-padding': true
            }"
          >
            <h4 class="text-center">
              Yes, they are graduating/have graduated!<br>
            </h4>
            <p v-if="!showUncategorizedBucket" class="text-center">
              Don't see someone in this list?
              <span
                class="clickable-paragraph emphasize-clickable"
                @click="getEligibleStudents(); modals.newGradForm = true"
              >
                Click here to add them!
              </span>
            </p>
          </div>
          <div class="col-md-2 min-padding">
            <h4 class="text-center">
              No! They're staying here.
            </h4>
          </div>
          <div class="col-md-2 min-padding">
            <h4 class="text-center">
              I'm not sure ...
            </h4>
          </div>
        </div>
        <div class="row">
          <div
            class="col-md-2 min-padding"
            v-if="showUncategorizedBucket"
          >
            <p class="text-center">
              <CircleButton
                disabled
                icon
                round
                class="red"
              >
                {{ uncategorizedStudents.length }}
              </CircleButton> to move
            </p>
          </div>
          <div
            :class="{
              'col-md-6': showUncategorizedBucket,
              'col-md-8': !showUncategorizedBucket,
              'min-padding': true
            }"
          >
            <p class="text-center">
              <CircleButton
                disabled
                icon
                round
                class="red"
              >
                {{ countIncompleteGrads }}
              </CircleButton> to complete
              <CircleButton
                disabled
                icon
                round
                class="green"
              >
                {{ countCompleteGrads }}
              </CircleButton> completed -
              <span
                class="clickable-paragraph emphasize-clickable"
                @click="modals.completeGradsModal = true"
              >
                See them here.
              </span>
            </p>
          </div>
          <div class="col-md-2 min-padding" />
          <div class="col-md-2 min-padding" />
        </div>
        <div class="row">
          <div
            class="col-md-2 min-padding scrollable-div"
            v-if="showUncategorizedBucket"
          >
            <draggable
              element="span"
              v-model="uncategorizedStudents"
              v-bind="dragOptions"
              :move="onMove"
              id="Uncategorized"
            >
              <transition-group name="no" class="list-group" tag="ul">
                <li
                  class="list-group-item min-padding card"
                  v-for="record in uncategorizedStudents"
                  :key="record.id"
                >
                  <div class="draggable-card-title">
                    {{ record.person }}
                  </div>
                  <div class="draggable-card-footer">
                    {{ record.involvement.involvement }}<br>
                    Leader: {{ record.involvement.bs_leader_discipler }}<br>
                    Missionary: {{ record.involvement.missionary }}
                  </div>
                </li>
              </transition-group>
            </draggable>
          </div>
          <div
            :class="{
              'col-md-6': showUncategorizedBucket,
              'col-md-8': !showUncategorizedBucket,
              'min-padding': true,
              'scrollable-div': true,
            }"
          >
            <div
              v-if="countIncompleteGrads == 0"
              class="draggable-card-title"
              style="margin-bottom:0"
            >
              Drag students here!
            </div>
            <draggable
              element="span"
              v-model="incompleteGrads"
              v-bind="dragOptions"
              :move="onMove"
              id="Graduating"
              status="incomplete"
            >
              <transition-group
                name="no"
                class="list-group"
                :class="{
                  'drag-target': countIncompleteGrads == 0
                }"
                style="padding-bottom: 50px;"
                tag="ul"
              >
                <li
                  class="list-group-item min-padding card"
                  v-for="record in incompleteGrads"
                  :key="record.id"
                >
                  <Form
                    :record="record"
                    :editing="record.id === editRecordId"
                    :allow-editing="editRecordId === null"
                    :graduation-month-options="graduationMonthOptions"
                    :graduation-year-options="graduationYearOptions"
                    @setEditRecordId="setEditRecordId($event)"
                    @setGraduationRecords="setGraduationRecords($event); editRecordId = null;"
                  />
                </li>
              </transition-group>
            </draggable>
          </div>
          <div
            class="col-md-2 min-padding scrollable-div"
          >
            <div
              v-if="remainingStudents.length == 0"
              class="draggable-card-title"
              style="margin-bottom:0"
            >
              Drag students here!
            </div>
            <draggable
              element="span"
              v-model="remainingStudents"
              v-bind="dragOptions"
              :move="onMove"
              id="Remaining"
            >
              <transition-group
                name="no"
                class="list-group"
                :class="{
                  'drag-target': remainingStudents.length == 0
                }"
                style="padding-bottom: 50px;"
                tag="ul"
              >
                <li
                  class="list-group-item min-padding card draggable-card-title"
                  v-for="record in remainingStudents"
                  :key="record.id"
                >
                  {{ record.person }}
                </li>
              </transition-group>
            </draggable>
          </div>
          <div
            class="col-md-2 min-padding scrollable-div"
          >
            <div
              v-if="unsureStudents.length == 0"
              class="draggable-card-title"
              style="margin-bottom:0"
            >
              Drag students here!
            </div>
            <draggable
              element="span"
              v-model="unsureStudents"
              v-bind="dragOptions"
              :move="onMove"
              id="Unsure"
            >
              <transition-group
                name="no"
                class="list-group"
                :class="{
                  'drag-target': unsureStudents.length == 0
                }"
                style="padding-bottom: 50px;"
                tag="ul"
              >
                <li
                  class="list-group-item min-padding card draggable-card-title"
                  v-for="record in unsureStudents"
                  :key="record.id"
                >
                  {{ record.person }}
                </li>
              </transition-group>
            </draggable>
          </div>
        </div>
      </div>

      <New
        :show="modals.newGradForm"
        :students="eligibleStudents"
        @close="modals.newGradForm = false"
        @newRecordSaved="setGraduationRecords($event); modals.newGradForm = false"
      />

      <Vocations
        :show="modals.vocations"
        :students="eligibleStudents"
        :vocations="vocationStudents"
        @close="modals.vocations = false; modals.otherVocations = false;"
        @newVocationSaved="setGraduationRecords($event)"
      />

      <Completed
        :show="modals.completeGradsModal"
        :complete-grads="completeGrads"
        :graduation-month-options="graduationMonthOptions"
        :graduation-year-options="graduationYearOptions"
        @setGraduationRecords="setGraduationRecords($event);"
        @close="modals.completeGradsModal = false"
      />

      <Modal
        :show="modals.otherVocations && !modals.vocations"
        :show-header="false"
      >
        <h4>
          You've completed all of the graduate forms! Are there any freshmen, sophomores,
          or juniors that are entering a religious community or seminary?
        </h4>
        <div>
          <p-button style="float: right" @click="modals.otherVocations = false;">
            No
          </p-button>
          <p-button
            style="float: right"
            class="custom-modal-type"
            @click="getEligibleStudents(); modals.otherVocations = false; modals.vocations = true"
          >
            Yes
          </p-button>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import Modal from '../../components/UIComponents/Modal.vue';
import CircleButton from '../../components/UIComponents/Button.vue';
import Form from '../../components/GraduationRecords/Form.vue';
import completedFormsModal from '../../components/GraduationRecords/CompletedFormsModal.vue';
import newGradRecord from '../../components/GraduationRecords/NewModal.vue';
import vocationsModal from '../../components/GraduationRecords/VocationsModal.vue';
import { RepositoryFactory } from '../../repositories/repositoryFactory';

const GraduationRecordsRepository = RepositoryFactory.get('graduationRecords');

export default {
    name: 'GRStep1',
    components: {
        draggable,
        Modal,
        CircleButton,
        Form,
        New: newGradRecord,
        Vocations: vocationsModal,
        Completed: completedFormsModal
    },
    data() {
        return {
            graduationRecords: [],
            uncategorizedStudents: [],
            graduatingStudents: [],
            remainingStudents: [],
            unsureStudents: [],
            vocationStudents: [],
            completeGrads: [],
            incompleteGrads: [],
            editable: true,
            isReady: false,
            transferring: false,
            isDragging: false,
            delayedDragging: false,
            editRecordId: null,
            graduationMonthOptions: [],
            graduationYearOptions: [],
            eligibleStudents: [],
            modals: {
                newGradForm: false,
                vocations: false,
                completeGradsModal: false,
                otherVocations: false,
                isFinished: false,
            },
            stepProgress: 0,
            error: null,
        };
    },
    watch: {
        isDragging(newValue) {
            if (newValue) {
                this.delayedDragging = true;
                return;
            }
            this.$nextTick(() => {
                this.delayedDragging = false;
            });
        },
        graduationRecords() {
            this.setLists();
            this.modals.isFinished = this.checkFinished();
            this.modals.otherVocations = this.checkFinished();
            this.checkProgress();
        }
    },
    computed: {
        dragOptions() {
            return {
                animation: 0,
                group: 'description',
                disabled: !this.editable,
                ghostClass: 'ghost'
            };
        },
        countCompleteGrads() {
            return this.completeGrads.length;
        },
        countIncompleteGrads() {
            return this.incompleteGrads.length;
        },
        countVocationStudents() {
            return this.vocationStudents.length;
        },
        showUncategorizedBucket() {
            return this.uncategorizedStudents.length > 0 || this.graduationRecords.length === 0;
        },
    },
    methods: {
        setLists() {
            this.uncategorizedStudents = [];
            this.graduatingStudents = [];
            this.remainingStudents = [];
            this.unsureStudents = [];
            if (this.graduationRecords) {
                this.uncategorizedStudents = this.graduationRecords.filter(
                    (record) => record.graduation_status == null
                );
                this.graduatingStudents = this.graduationRecords.filter(
                    (record) => record.graduation_status === 'Graduating'
                );
                this.remainingStudents = this.graduationRecords.filter(
                    (record) => record.graduation_status === 'Remaining'
                );
                this.unsureStudents = this.graduationRecords.filter(
                    (record) => record.graduation_status === 'Unsure'
                );
                this.isReady = true;
            }
            this.incompleteGrads = this.graduationRecords.filter((record) => record.graduation_status === 'Graduating');
            this.incompleteGrads = this.incompleteGrads.filter((record) => !record.phone
                || !record.email || !record.graduation_month || !record.graduation_year);
            this.completeGrads = this.graduationRecords.filter((record) => record.graduation_status === 'Graduating');
            this.completeGrads = this.completeGrads.filter((record) => record.phone && record.email
                && record.graduation_month && record.graduation_year);
            this.vocationStudents = this.graduationRecords;
            this.vocationStudents = this.vocationStudents.filter(
                (record) => record.vocation.is_entering_religious === true
                  || record.vocation.is_entering_seminary === true
            );
        },
        onMove({ relatedContext, draggedContext }) {
            if (relatedContext.component.$attrs.id === 'Uncategorized') {
                return false;
            }
            if (this.transferring === false) {
                this.transferring = true;
                // console.log(draggedContext); // for testing
                // console.log(relatedContext.component.$attrs.id);

                GraduationRecordsRepository.updateGraduationRecords(
                    [
                        {
                            id: draggedContext.element.id,
                            graduation_status: relatedContext.component.$attrs.id
                        }
                    ]
                )
                    .then((response) => {
                        draggedContext.element.status = relatedContext.component.$attrs.id;
                        this.transferring = false;
                        const relatedElement = relatedContext.element;
                        const draggedElement = draggedContext.element;
                        return (
                            (!relatedElement || !relatedElement.fixed) && !draggedElement.fixed
                        );
                    })
                    .catch((error) => {
                        // Bugsnag - cannot read message of undefined
                        this.$message(error.message);
                        this.transferring = false;
                    });
            } else {
                return false;
            }
            return true;
        },
        setEditRecordId(editRecordId) {
            this.editRecordId = editRecordId;
        },
        setGraduationRecords(data) {
            GraduationRecordsRepository.getGraduationRecords()
                .then((response) => {
                    this.graduationRecords = response.data.data;
                })
                .catch((error) => {
                    this.error = error.response.data.message;
                    this.isReady = true;
                });
        },
        getEligibleStudents() {
            if (!this.eligibleStudents.length) {
                GraduationRecordsRepository.getEligibleStudents()
                    .then((response) => {
                        this.eligibleStudents = response.data.data;
                    })
                    .catch((error) => {
                        // Bugsnag - cannot read message of undefined
                        this.$message(error.message);
                    });
            }
        },
        checkFinished() {
            if (this.uncategorizedStudents.length === 0 && this.countIncompleteGrads === 0) {
                this.modals.otherVocations = true;
            }
        },
        checkProgress() {
            if (this.checkFinished()) {
                this.stepProgress = 100;
            } else if (this.uncategorizedStudents.length === 0) {
                this.stepProgress = 50;
            } else {
                this.stepProgress = 0;
            }
        },
        triggerAnimation() {}
    },
    created() {
        GraduationRecordsRepository.getGraduationRecords()
            .then((response) => {
                this.graduationRecords = response.data.data;
            })
            .catch((error) => {
                // Bugsnag - cannot read response of undefined
                this.error = error.response.data.message;
                this.isReady = true;
            });
        GraduationRecordsRepository.getGraduationOptions()
            .then((response) => {
                this.graduationMonthOptions = response.data.graduationMonths;
                this.graduationYearOptions = response.data.graduationYears;
            });
    }
};
</script>

<style>
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.list-group {
  min-height: 20px;
}
.list-group-item {
  cursor: move;
}
.list-group-item i {
  cursor: pointer;
}
.dc-headings {
    position: relative;
    display: block;
    padding: .75rem 1.25rem;
    margin-bottom: -1px;
    background-color: #77ab59;
    color: white;
    border: 1px solid rgba(0,0,0,.125);
}
#loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 15%;
  padding: 20px;
}
.no-padding {
  padding: 0;
}
.min-lr-padding {
  padding: 0 .5rem;
}
.min-padding {
  padding: .5rem;
}
.min-margin {
  margin: .5rem;
}
.hide {
    display: none;
}
.el-input.fit-input .el-input__inner,
.select-default.el-select.fit-input,
.select-default.el-select.fit-input .el-input:hover .el-input__icon {
    padding: 0 5px !important;
    margin: 0px !important;
}
.fit-input > .el-input {
  margin: 0 !important;
}
.list-group-item.card:first-child, .list-group-item.card:last-child {
  border-radius: 12px !important;
}
.draggable-card-title {
  font-size: 1rem !important;
  font-weight: bold;
  margin-bottom: 1rem;
  text-align: center;
}
.draggable-card-body {
  font-size: .75rem;
}
.draggable-card-footer {
  font-size: .75rem !important;
  color: gray;
}
.draggable-card-footer.horizontal {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 1rem;
}
.fit-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align:center;
}
.el-checkbox__label {
  padding-left: 2px !important;
}
.drag-target {
  outline: 1px dotted blue;
  outline-offset: -5px;
}
.drag-students {
  width: 100%;
  text-align: center;
  padding: 1rem 0;
  margin: 0;
}
.red {
  background-color: #F56C6C !important;
  opacity: 1 !important;
  color: white;
}
.green {
  background-color: #6cc070 !important;
  opacity: 1 !important;
  color: white;
  margin: 0px;
}
.lrg {
  font-size: 1.5rem;
}
.prog-step {
  text-align: center;
}
</style>
<style scoped>
/* Override Bootstrap container max-widths */
@media (min-width: 768px) {
  .container {
    max-width: 100%;
  }
}
@media (min-width: 576) {
  .container {
    max-width: 100%;
  }
}
.col-md-4 {
  margin: 0 !important;
}
h4 {
  margin: 2rem 0 0 0;
  padding: 0;
}
p {
  margin: 0;
}
/* Hide for mobile */
@media only screen and (max-width: 770px) {
  .display-desktop {
    display: none;
  }
}
@media only screen and (min-width: 770px) {
  .display-mobile {
    display: none;
  }
}

.scrollable-div {
  margin: 0;
  overflow-y: auto;
  height: 100vh;
}
::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 7px;
}

::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, .5);
  box-shadow: 0 0 1px rgba(255, 255, 255, .5);
}
</style>
